import { useCallback } from 'react';
import * as yup from 'yup';
import { nullTypeError } from 'utils/yup';
import { useParams, useHistory } from 'react-router-dom';
import { Formik, Field, Form, type FormikHelpers } from 'formik';
import Button from 'components/common/Button';
import { useIntl, FormattedMessage } from 'react-intl';
import useResetPassword from 'hooks/auth/useResetPassword';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { toast } from 'react-toastify';
import routePaths from 'router/route-paths';
import { QuickCalculatorStep3 } from 'assets/icons/QuickCalculator';
import { ArrowForward } from 'assets/icons';
import PageTitle from 'components/common/PageTitle';
import PasswordInput from 'components/inputs/PasswordInput';
import { messageOf } from 'utils/error';

interface IResetPasswordFormValues {
  password: string;
  repeatedPassword: string;
}

const resetPasswordShape = {
  password: yup
    .string()
    .required(() => <FormattedMessage id="errors.password_required" />)
    .min(8, () => <FormattedMessage id="errors.password_too_small" />)
    .nonNullable(nullTypeError('string')),
  repeatedPassword: yup
    .string()
    .required(() => <FormattedMessage id="errors.passwords_must_match" />)
    // The former list also held null, which a non-nullable schema never gets to compare.
    .oneOf([yup.ref('password')], () => <FormattedMessage id="errors.passwords_must_match" />)
    .nonNullable(nullTypeError('string')),
};

const useResetPasswordSubmitHandler = (token: string) => {
  const resetPassword = useResetPassword();
  const history = useHistory();

  const handleSubmit = useCallback(
    async (
      { password }: IResetPasswordFormValues,
      { resetForm }: FormikHelpers<IResetPasswordFormValues>,
    ) => {
      try {
        await resetPassword({ token, password });
        history.replace(routePaths.myProjects);
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        resetForm();
      }
    },
    [history, resetPassword, token],
  );

  return handleSubmit;
};

const resetPasswordSchema = yup.object().shape(resetPasswordShape);

const initialValues: IResetPasswordFormValues = { password: '', repeatedPassword: '' };

const ResetPassword = () => {
  const { formatMessage: t } = useIntl();
  const params = useParams<{ token: string }>();

  const onSubmit = useResetPasswordSubmitHandler(params.token);

  return (
    <Grid className="mt-8">
      <Row between="lg" center="md">
        <Col xs={0} sm={0} md={0} lg={6} xl={5}>
          <QuickCalculatorStep3
            width="100%"
            title={t({ id: 'alt_text.auth_pages.reset_password' })}
          />
        </Col>
        <Col md={9} lg={6} xl={5}>
          <Formik<IResetPasswordFormValues>
            initialValues={initialValues}
            validationSchema={resetPasswordSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <>
                <PageTitle auth>{t({ id: 'page_titles.reset_password' })}</PageTitle>
                <Form>
                  <Field
                    label={t({ id: 'auth.new_password' })}
                    type="password"
                    name="password"
                    component={PasswordInput}
                  />
                  <Field
                    label={t({ id: 'auth.repeat_new_password' })}
                    type="password"
                    name="repeatedPassword"
                    component={PasswordInput}
                  />
                  <div className="text-right mt-3">
                    <Button
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      type="submit"
                      endIcon={<ArrowForward />}
                    >
                      {t({ id: 'auth.forgot_password.reset_password' })}
                    </Button>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </Col>
      </Row>
    </Grid>
  );
};

export default ResetPassword;
